import React, { useState, useEffect, useRef } from 'react';
import Header from './components/Header';
import MarketTab from './components/MarketTab';
import PortfolioTab from './components/PortfolioTab';
import SettingsModal from './components/SettingsModal';
import AddCoinModal from './components/AddCoinModal';
import CoinDetailModal from './components/CoinDetailModal';
import DonateModal from './components/DonateModal';
import SuggestionsModal from './components/SuggestionsModal';
import PriceComparisonModal from './components/PriceComparisonModal';
import Footer from './components/Footer';
import { useCryptoData } from './hooks/useCryptoData';
import { usePortfolio } from './hooks/usePortfolio';
import { useSettings } from './hooks/useSettings';
import { useWatchlist } from './hooks/useWatchlist';
import { usePriceAlerts } from './hooks/usePriceAlerts';

function App() {
  const [activeTab, setActiveTab] = useState<'market' | 'portfolio'>('market');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addCoinOpen, setAddCoinOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<string>('market_cap');
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showPriceComparison, setShowPriceComparison] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { settings, updateSettings } = useSettings();
  const { cryptoData, loading, error, refresh } = useCryptoData(settings.currency || 'usd');
  const { 
    portfolio, 
    portfolios,
    currentPortfolioId,
    addCoin, 
    removeCoin, 
    refreshPortfolio,
    createPortfolio,
    deletePortfolio,
    renamePortfolio,
    switchPortfolio
  } = usePortfolio();
  const { isInWatchlist, addToWatchlist } = useWatchlist();
  const { alerts, triggeredAlerts, createAlert, deleteAlert, getAlertsForCoin, requestNotificationPermission } = usePriceAlerts(cryptoData);

  const unlockPortfolioAnalytics = async () => {
    if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
      alert('Connect MetaNet wallet');
      return;
    }

    try {
      const amount = 500; // 500 satoshis to unlock portfolio analytics
      const result = await window.bsvPayments.requestPayment('PORTFOLIO_ANALYTICS', amount);
      
      if (result) {
        updateSettings({ portfolioAnalyticsUnlocked: true });
      }
    } catch (error) {
      console.error('Portfolio analytics unlock failed:', error);
    }
  };

  const handlePriceComparisonPayment = async (): Promise<boolean> => {
    if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
      alert('Payment system not available. Please refresh the page.');
      return false;
    }

    try {
      const amount = 300; // 300 satoshis per comparison session
      const result = await window.bsvPayments.requestPayment('PRICE_COMPARISON', amount);
      return !!result;
    } catch (error) {
      console.error('Price comparison payment failed:', error);
      return false;
    }
  };

  const unlockAdvancedPortfolioAnalytics = async (): Promise<boolean> => {
    if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
      alert('Payment system not available. Please refresh the page.');
      return false;
    }

    try {
      const amount = window.bsvPayments.getPaymentAmount('ADVANCED_PORTFOLIO_ANALYTICS');
      const result = await window.bsvPayments.requestPayment('ADVANCED_PORTFOLIO_ANALYTICS', amount);
      return !!result;
    } catch (error) {
      console.error('Advanced portfolio analytics payment failed:', error);
      return false;
    }
  };

  const unlockMultiplePortfolios = async (): Promise<boolean> => {
    if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
      alert('Payment system not available. Please refresh the page.');
      return false;
    }

    try {
      const amount = window.bsvPayments.getPaymentAmount('MULTIPLE_PORTFOLIO');
      const result = await window.bsvPayments.requestPayment('MULTIPLE_PORTFOLIO', amount);
      
      if (result) {
        updateSettings({ multiplePortfoliosUnlocked: true });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Multiple portfolios unlock failed:', error);
      return false;
    }
  };

  const handleCreatePortfolio = async (name: string): Promise<string> => {
    return createPortfolio(name);
  };

  // Refresh data when currency changes (if unlocked or USD)
  useEffect(() => {
    if (settings.currency && (settings.currency === 'usd' || settings.currencyUnlocked)) {
      refresh();
    }
  }, [settings.currency]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        // Only handle '/' to focus search when not already in an input
        if (e.key === '/' && target.tagName !== 'INPUT') {
          e.preventDefault();
          searchInputRef.current?.focus();
        }
        return;
      }

      switch (e.key) {
        case '/':
          e.preventDefault();
          searchInputRef.current?.focus();
          break;
        case 'Escape':
          if (selectedCoin) {
            setSelectedCoin(null);
          } else if (settingsOpen) {
            setSettingsOpen(false);
          } else if (addCoinOpen) {
            setAddCoinOpen(false);
          } else if (donateOpen) {
            setDonateOpen(false);
          }
          break;
        case '?':
          e.preventDefault();
          setShowKeyboardShortcuts(true);
          break;
        case '1':
          if (e.metaKey || e.ctrlKey) break;
          e.preventDefault();
          setActiveTab('market');
          break;
        case '2':
          if (e.metaKey || e.ctrlKey) break;
          e.preventDefault();
          setActiveTab('portfolio');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCoin, settingsOpen, addCoinOpen, donateOpen, suggestionsOpen]);

  const handleAddCoin = (coinId: string, quantity: number, purchasePrice: number | null) => {
    addCoin(coinId, quantity, purchasePrice, cryptoData);
  };

  return (
    <div className="min-h-screen p-6 font-sans bg-gradient-to-br from-bg via-bg-secondary to-bg-tertiary text-text">
      <div className="container max-w-7xl mx-auto">
        <Header 
          onRefresh={refresh}
          onSettingsClick={() => setSettingsOpen(true)}
          onDonateClick={() => setDonateOpen(true)}
          onSuggestionsClick={() => setSuggestionsOpen(true)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchInputRef={searchInputRef}
        />

        <div className="flex gap-3 mb-8 p-2 bg-bg-secondary rounded-full w-fit shadow-lg">
          <button
            onClick={() => setActiveTab('market')}
            className={`px-8 py-4 border-none rounded-full text-base font-semibold cursor-pointer transition-all duration-300 ${
              activeTab === 'market'
                ? 'bg-gradient-to-r from-primary to-primary-light text-white shadow-bubble-lg scale-105'
                : 'bg-transparent text-text-secondary hover:text-primary hover:scale-105'
            }`}
          >
            💹 Market
          </button>
          <button
            onClick={() => setActiveTab('portfolio')}
            className={`px-8 py-4 border-none rounded-full text-base font-semibold cursor-pointer transition-all duration-300 ${
              activeTab === 'portfolio'
                ? 'bg-gradient-to-r from-primary to-primary-light text-white shadow-bubble-lg scale-105'
                : 'bg-transparent text-text-secondary hover:text-primary hover:scale-105'
            }`}
          >
            💼 Portfolio
          </button>
          <button
            onClick={() => setShowPriceComparison(true)}
            className="px-6 py-4 bg-gradient-to-r from-primary to-primary-light border-none rounded-full text-white text-sm font-semibold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95 flex items-center gap-2"
            title="Compare Coins (300 sats)"
          >
            ⚖️ Compare
          </button>
        </div>

        {activeTab === 'market' && (
          <MarketTab
            cryptoData={cryptoData}
            loading={loading}
            error={error}
            onCoinClick={(coinId) => {
              console.log('App: Coin clicked, setting selectedCoin to:', coinId);
              setSelectedCoin(coinId);
            }}
            searchQuery={searchQuery}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            isInWatchlist={isInWatchlist}
            onWatchlistToggle={addToWatchlist}
            currency={settings.currency}
          />
        )}

        {activeTab === 'portfolio' && (
          <PortfolioTab
            portfolio={portfolio}
            portfolios={portfolios}
            currentPortfolioId={currentPortfolioId}
            onAddCoin={() => setAddCoinOpen(true)}
            onRemoveCoin={removeCoin}
            onSwitchPortfolio={switchPortfolio}
            onCreatePortfolio={handleCreatePortfolio}
            onDeletePortfolio={deletePortfolio}
            onRenamePortfolio={renamePortfolio}
            currency={settings.currency}
            analyticsUnlocked={settings.portfolioAnalyticsUnlocked || false}
            onUnlockAnalytics={unlockPortfolioAnalytics}
            onUnlockAdvancedAnalytics={unlockAdvancedPortfolioAnalytics}
            multiplePortfoliosUnlocked={settings.multiplePortfoliosUnlocked || false}
            onUnlockMultiplePortfolios={unlockMultiplePortfolios}
          />
        )}

        <SettingsModal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          settings={settings}
          onUpdateSettings={updateSettings}
        />

        <AddCoinModal
          isOpen={addCoinOpen}
          onClose={() => setAddCoinOpen(false)}
          onAddCoin={handleAddCoin}
          cryptoData={cryptoData}
          currency={settings.currency}
        />

        <DonateModal
          isOpen={donateOpen}
          onClose={() => setDonateOpen(false)}
          cryptoData={cryptoData}
        />

        <SuggestionsModal
          isOpen={suggestionsOpen}
          onClose={() => setSuggestionsOpen(false)}
        />

        {selectedCoin && (
          <CoinDetailModal
            coinId={selectedCoin}
            isOpen={!!selectedCoin}
            onClose={() => {
              console.log('App: Closing modal, clearing selectedCoin');
              setSelectedCoin(null);
            }}
            cryptoData={cryptoData}
            currency={settings.currency}
            priceAlerts={getAlertsForCoin(selectedCoin)}
            onCreateAlert={createAlert}
            onDeleteAlert={deleteAlert}
          />
        )}

        <Footer sortBy={activeTab === 'market' ? sortBy : undefined} />

        <PriceComparisonModal
          isOpen={showPriceComparison}
          onClose={() => setShowPriceComparison(false)}
          cryptoData={cryptoData}
          currency={settings.currency}
          onRequestPayment={handlePriceComparisonPayment}
        />

        {showKeyboardShortcuts && (
          <div
            className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setShowKeyboardShortcuts(false)}
          >
            <div
              className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-lg w-[90%] max-h-[90vh] overflow-y-auto relative shadow-bubble-lg border-2 border-primary/20"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowKeyboardShortcuts(false)}
                className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center z-10"
              >
                &times;
              </button>

              <div className="text-center mb-8">
                <div className="text-6xl mb-4">⌨️</div>
                <h2 className="text-3xl mb-3 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
                  Keyboard Shortcuts
                </h2>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-bg rounded-2xl border-2 border-border">
                  <span className="text-text-secondary font-medium">Focus Search</span>
                  <kbd className="px-3 py-1.5 bg-bg-tertiary border-2 border-border rounded-lg text-text font-mono text-sm font-bold">/</kbd>
                </div>
                <div className="flex justify-between items-center p-4 bg-bg rounded-2xl border-2 border-border">
                  <span className="text-text-secondary font-medium">Close Modals</span>
                  <kbd className="px-3 py-1.5 bg-bg-tertiary border-2 border-border rounded-lg text-text font-mono text-sm font-bold">Esc</kbd>
                </div>
                <div className="flex justify-between items-center p-4 bg-bg rounded-2xl border-2 border-border">
                  <span className="text-text-secondary font-medium">Go to Market</span>
                  <kbd className="px-3 py-1.5 bg-bg-tertiary border-2 border-border rounded-lg text-text font-mono text-sm font-bold">1</kbd>
                </div>
                <div className="flex justify-between items-center p-4 bg-bg rounded-2xl border-2 border-border">
                  <span className="text-text-secondary font-medium">Go to Portfolio</span>
                  <kbd className="px-3 py-1.5 bg-bg-tertiary border-2 border-border rounded-lg text-text font-mono text-sm font-bold">2</kbd>
                </div>
                <div className="flex justify-between items-center p-4 bg-bg rounded-2xl border-2 border-border">
                  <span className="text-text-secondary font-medium">Show Shortcuts</span>
                  <kbd className="px-3 py-1.5 bg-bg-tertiary border-2 border-border rounded-lg text-text font-mono text-sm font-bold">?</kbd>
                </div>
              </div>

              <div className="mt-8 text-center text-text-secondary/70 text-sm">
                Press <kbd className="px-2 py-1 bg-bg-tertiary border border-border rounded text-xs">Esc</kbd> or click outside to close
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;

